import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import SignatureCanvas from 'react-signature-canvas';
import { deriveFormOptions, deriveQuestionsForForm, QUESTION_TYPES } from '../utils/occurrenceHelpers';
import { siteSelectStyles } from '../utils/selectStyles';
import { useFollowUpOccurrence } from '../hooks/useFollowUpOccurrence';

function FollowUpForm({ parentIncidentID, allIncidents, onSuccess, onCancel }) {
    const [selectedForm, setSelectedForm] = useState(null);
    const [answers, setAnswers] = useState({});
    const [notes, setNotes] = useState('');

    const sigRefs = useRef({});
    const { submitting, submitError, submitSuccess, submitFollowUp, clearError } = useFollowUpOccurrence();

    const formOptions = useMemo(() =>
        deriveFormOptions(allIncidents).map(f => ({
            value: f.formID,
            label: f.formTitle,
        })),
    [allIncidents]);

    const questions = useMemo(() => {
        if (!selectedForm) return [];
        return deriveQuestionsForForm(allIncidents, selectedForm.value);
    }, [allIncidents, selectedForm]);

    useEffect(() => {
        setAnswers({});
    }, [selectedForm]);

    const handleAnswerChange = useCallback((questionID, value) => {
        setAnswers(prev => ({ ...prev, [questionID]: value }));
    }, []);

    const handleSubmit = useCallback(async () => {
        clearError();
        const payload = {
            ParentIncidentID: parentIncidentID,
            FormID: selectedForm.value,
            Notes: notes,
            IncLat: null,
            IncLong: null,
            Answers: questions.map(q => ({
                QuestionID: q.questionID,
                Answer: String(answers[q.questionID] ?? ''),
            })),
        };

        const newId = await submitFollowUp(payload);
        if (newId) {
            setTimeout(() => onSuccess?.(newId), 2000);
        }
    }, [parentIncidentID, selectedForm, notes, questions, answers, submitFollowUp, clearError, onSuccess]);

    const renderQuestionInput = (q) => {
        const val = answers[q.questionID] ?? '';

        switch (Number(q.questionType)) {
            case QUESTION_TYPES.YES_NO:
                return (
                    <div className="occurrence-followup__toggle">
                        <button
                            type="button"
                            className={`occurrence-followup__toggle-btn ${val === 'Yes' ? 'occurrence-followup__toggle-btn--active' : ''}`}
                            onClick={() => handleAnswerChange(q.questionID, 'Yes')}
                        >
                            Yes
                        </button>
                        <button
                            type="button"
                            className={`occurrence-followup__toggle-btn ${val === 'No' ? 'occurrence-followup__toggle-btn--active' : ''}`}
                            onClick={() => handleAnswerChange(q.questionID, 'No')}
                        >
                            No
                        </button>
                    </div>
                );

            case QUESTION_TYPES.TICK_BOX:
                return (
                    <label className="occurrence-followup__checkbox">
                        <input
                            type="checkbox"
                            checked={val === 'Yes' || val === true}
                            onChange={(e) => handleAnswerChange(q.questionID, e.target.checked ? 'Yes' : 'No')}
                        />
                        <span>{val === 'Yes' || val === true ? 'Yes' : 'No'}</span>
                    </label>
                );

            case QUESTION_TYPES.NUMERIC:
                return (
                    <input
                        type="number"
                        className="occurrence-followup__input"
                        value={val}
                        onChange={(e) => handleAnswerChange(q.questionID, e.target.value)}
                    />
                );

            case QUESTION_TYPES.DATE:
                return (
                    <input
                        type="date"
                        className="occurrence-followup__input"
                        value={val}
                        onChange={(e) => handleAnswerChange(q.questionID, e.target.value)}
                    />
                );

            case QUESTION_TYPES.TIME:
                return (
                    <input
                        type="time"
                        className="occurrence-followup__input"
                        value={val}
                        onChange={(e) => handleAnswerChange(q.questionID, e.target.value)}
                    />
                );

            case QUESTION_TYPES.DATE_TIME:
                return (
                    <input
                        type="datetime-local"
                        className="occurrence-followup__input"
                        value={val}
                        onChange={(e) => handleAnswerChange(q.questionID, e.target.value)}
                    />
                );

            case QUESTION_TYPES.SIGNATURE:
                return (
                    <div className="occurrence-followup__signature">
                        <SignatureCanvas
                            ref={(ref) => { sigRefs.current[q.questionID] = ref; }}
                            penColor="#fff"
                            backgroundColor="rgba(255,255,255,0.06)"
                            canvasProps={{ className: 'occurrence-followup__sig-canvas' }}
                            onEnd={() => {
                                const ref = sigRefs.current[q.questionID];
                                if (ref && !ref.isEmpty()) {
                                    handleAnswerChange(q.questionID, ref.toDataURL('image/png'));
                                }
                            }}
                        />
                        <button
                            type="button"
                            className="occurrence-followup__sig-clear"
                            onClick={() => {
                                const ref = sigRefs.current[q.questionID];
                                if (ref) {
                                    ref.clear();
                                    handleAnswerChange(q.questionID, '');
                                }
                            }}
                        >
                            Clear
                        </button>
                    </div>
                );

            case QUESTION_TYPES.MAP:
                return <p className="occurrence-followup__unsupported">Map input not supported in web view</p>;

            case QUESTION_TYPES.IMAGE:
                return (
                    <div>
                        <input
                            type="file"
                            accept="image/*"
                            className="occurrence-followup__input"
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                const reader = new FileReader();
                                reader.onload = () => handleAnswerChange(q.questionID, reader.result);
                                reader.readAsDataURL(file);
                            }}
                        />
                        {val && <img src={val} alt="Preview" className="occurrence-followup__image-preview" />}
                    </div>
                );

            case QUESTION_TYPES.TEXT:
            default:
                return (
                    <textarea
                        className="occurrence-followup__textarea"
                        value={val}
                        onChange={(e) => handleAnswerChange(q.questionID, e.target.value)}
                        rows={2}
                    />
                );
        }
    };

    if (submitSuccess) {
        return (
            <div className="occurrence-followup">
                <div className="occurrence-followup__success">
                    Follow-up occurrence <strong>#{submitSuccess}</strong> created successfully.
                </div>
            </div>
        );
    }

    return (
        <div className="occurrence-followup">
            <div className="occurrence-followup__header">
                <h4>Follow-Up Occurrence</h4>
                <p>Following up on Occurrence ID: <strong>{parentIncidentID}</strong></p>
            </div>

            <div className="occurrence-followup__form-select">
                <label className="occurrence-followup__label">Occurrence Form</label>
                <Select
                    value={selectedForm}
                    onChange={setSelectedForm}
                    options={formOptions}
                    placeholder="Select a form..."
                    isClearable
                    isSearchable
                    styles={siteSelectStyles}
                />
            </div>

            {questions.length > 0 && (
                <div className="occurrence-followup__questions">
                    {questions.map(q => (
                        <div key={q.questionID} className="occurrence-followup__question">
                            <label className="occurrence-followup__label">{q.questionDescription}</label>
                            {renderQuestionInput(q)}
                        </div>
                    ))}
                </div>
            )}

            <div className="occurrence-followup__notes">
                <label className="occurrence-followup__label">Follow-Up Notes</label>
                <textarea
                    className="occurrence-followup__textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder="Add any follow-up notes..."
                />
            </div>

            {submitError && (
                <div className="occurrence-followup__error">{submitError}</div>
            )}

            <div className="occurrence-followup__actions">
                <button
                    type="button"
                    className="occurrence-followup__cancel-btn"
                    onClick={onCancel}
                    disabled={submitting}
                >
                    Cancel
                </button>
                <button
                    type="button"
                    className="occurrence-followup__submit-btn"
                    onClick={handleSubmit}
                    disabled={submitting || !selectedForm}
                >
                    {submitting ? 'Creating...' : 'Create Follow-Up'}
                </button>
            </div>
        </div>
    );
}

FollowUpForm.propTypes = {
    parentIncidentID: PropTypes.number.isRequired,
    allIncidents: PropTypes.array.isRequired,
    onSuccess: PropTypes.func,
    onCancel: PropTypes.func,
};

export default FollowUpForm;
